// src/components/maps/MapPicker.js
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../../lib/googleMapsLoader";

const DEFAULT_RIYADH = { lat: 24.7136, lng: 46.6753 };

export default function MapPicker({
  value,
  onChange,
  height = 260,
  className = "",
  defaultCenter = DEFAULT_RIYADH,
  defaultZoom = 12,
  userCenter,
  userZoom = 16,
  disabled = false,
  active = true,
  enableMyLocationButton = true,
  allowFullscreen = true,
  radiusKm = null,
  showRadiusCircle = false,
}) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const clickListenerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");

  const isValidLatLng = (p) => {
    const lat = Number(p?.lat);
    const lng = Number(p?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng);
  };

  const toMeters = (km) => {
    const n = Number(km);
    return Number.isFinite(n) && n > 0 ? n * 1000 : null;
  };

  // ---------------- INIT MAP ----------------
  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    (async () => {
      try {
        await loadGoogleMaps();
        if (cancelled) return;

        const { Map } = await window.google.maps.importLibrary("maps");

        const initialCenter = isValidLatLng(value)
          ? value
          : isValidLatLng(userCenter)
          ? userCenter
          : defaultCenter;

        const initialZoom = isValidLatLng(value)
          ? 17
          : isValidLatLng(userCenter)
          ? userZoom
          : defaultZoom;

        if (!mapRef.current) {
          mapRef.current = new Map(mapDivRef.current, {
            center: initialCenter,
            zoom: initialZoom,
            clickableIcons: false,
            fullscreenControl: !!allowFullscreen,
            gestureHandling: "greedy",
            streetViewControl: false,
            mapTypeControl: false,
          });
        } else {
          mapRef.current.setCenter(initialCenter);
          mapRef.current.setZoom(initialZoom);
        }

        setReady(true);
        setErr("");
      } catch (e) {
        if (!cancelled) setErr("Failed to load map");
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    allowFullscreen,
    defaultCenter?.lat,
    defaultCenter?.lng,
    defaultZoom,
    userCenter?.lat,
    userCenter?.lng,
    userZoom,
    value?.lat,
    value?.lng,
  ]);

  // ---------------- MARKER ----------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const map = mapRef.current;
    const fallback = map.getCenter();
    const lat = value?.lat ?? fallback?.lat();
    const lng = value?.lng ?? fallback?.lng();

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        draggable: !disabled,
      });

      markerRef.current.addListener("dragend", () => {
        const p = markerRef.current.getPosition();
        onChange?.({ lat: p.lat(), lng: p.lng() });
      });
    } else {
      markerRef.current.setMap(map);
      markerRef.current.setPosition({ lat, lng });
      markerRef.current.setDraggable(!disabled);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, value?.lat, value?.lng, disabled]);

  // ---------------- CIRCLE ----------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const meters = toMeters(radiusKm);
    const center = isValidLatLng(value) ? value : null;

    if (!showRadiusCircle || !meters || !center) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      return;
    }

    if (!circleRef.current) {
      circleRef.current = new window.google.maps.Circle({
        map: mapRef.current,
        center,
        radius: meters,
        strokeColor: "#006C35",
        strokeWeight: 2,
        fillColor: "#006C35",
        fillOpacity: 0.15,
      });
    } else {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(meters);
      circleRef.current.setMap(mapRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, value?.lat, value?.lng, radiusKm, showRadiusCircle]);

  // ---------------- CLICK ----------------
  useEffect(() => {
    if (!ready || !mapRef.current || disabled) return;

    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current);
    }

    clickListenerRef.current = mapRef.current.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      markerRef.current.setPosition({ lat, lng });
      onChange?.({ lat, lng });
    });

    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, disabled]);

  // ---------------- FULL CLEANUP ----------------
  useEffect(() => {
    return () => {
      if (clickListenerRef.current)
        window.google?.maps?.event?.removeListener(clickListenerRef.current);

      if (markerRef.current) markerRef.current.setMap(null);
      if (circleRef.current) circleRef.current.setMap(null);

      markerRef.current = null;
      circleRef.current = null;
      mapRef.current = null;
    };
  }, []);

  if (err) {
    return (
      <div className="border p-3 text-red-600 bg-white rounded-lg">
        {err}
      </div>
    );
  }

  return (
    <div
      className={`relative border rounded-lg overflow-hidden ${className}`}
      style={{ height }}
    >
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
